import useSWR, { SWRResponse } from 'swr';
import isEqual from 'lodash/isEqual';
import { fetchEffectiveCart } from 'api/cart';
import { useMemo } from 'react';
import { useEffectiveCartPayload } from 'hooks/effective-cart/useEffectiveCart';
import { MINIMUM_PRODUCTS_IN_BUNDLE } from 'components/dynamic-bundle/constants';
import { Bundle, BundleVariant } from 'components/dynamic-bundle/utils';
import { EffectiveCart } from 'interfaces/cart';
import { useSavingPlanStore } from 'stores/saving-plan';
import {
  BundleTotals,
  getBundleTotals,
} from 'modules/cart/components/cart-items/cart-bundle/utils';
import { useDebouncedValue } from 'hooks/common/use-debounced-value';
import { useRootStore } from 'stores/use-root-store';

interface UseGetPrices {
  bundleItems: BundleVariant[];
  sumOfProducts: number;
  dictionary: Bundle['dictionary'] | null;
}

/**
 * Sends bundle items as part of fetchEffectiveCart request, returns SWRResponse
 */
export const useGetPrices = ({
  bundleItems,
  sumOfProducts,
  dictionary,
}: UseGetPrices): SWRResponse<EffectiveCart & BundleTotals> => {
  const activePlan = useSavingPlanStore((store) => store.activePlan);
  const petType = useRootStore((store) => store.petType);
  const bundleItemsForEffectiveCart = useMemo(() => {
    if (bundleItems === null || dictionary === null) {
      return [];
    }
    // TODO: it looks really similar to CartItemInput except variantId is number
    return bundleItems?.map((item) => {
      return {
        variantId: Number(item.id),
        quantity: dictionary[item.category].amountPerItem,
        bundleRefs: item.bundleRefs,
        petType,
      };
    });
  }, [bundleItems, dictionary, petType]);

  const effectiveCartPayload = useEffectiveCartPayload({
    shippingAddress: null,
    shippingMethod: null,
    useCredits: false,
    items: bundleItemsForEffectiveCart,
  });

  const { value: debouncedPayload } = useDebouncedValue({
    value: effectiveCartPayload,
    ms: 300,
  });

  const prices = useSWR(
    sumOfProducts < MINIMUM_PRODUCTS_IN_BUNDLE || bundleItems.length === 0
      ? null
      : ['fetchDynamicBundle', debouncedPayload],
    async () => {
      const result = await fetchEffectiveCart({
        ...debouncedPayload,
        ruleId: activePlan?.ruleId,
      });

      return {
        ...result,
        ...getBundleTotals({ items: result.items }),
      };
    }
  );

  return {
    ...prices,
    isLoading: prices.isLoading || !isEqual(debouncedPayload, effectiveCartPayload),
  };
};
